/**
 * Pure WHIP publish helper for live talk-down (ENG-1721). Mirrors the live
 * player's WHEP exchange (useWhepLiveStream.playWhep): one offer, wait for
 * ICE gathering (no trickle), POST the SDP, apply the answer. Differences:
 * one sendonly audio transceiver (Opus preferred), no 404 cold-start retry
 * (the browser is the publisher, so there is nothing to wait for), and the
 * `Location` header is kept so the resource can be DELETEd.
 *
 * `fetch` and the RTCPeerConnection constructor are injectable for tests.
 * The WHIP URL already carries `?jwt=<token>` (same as the WHEP player), so
 * no Authorization header is sent.
 */

export interface WhipDeps {
  fetch?: typeof fetch;
  createPeerConnection?: (config?: RTCConfiguration) => RTCPeerConnection;
  /** Cap on waiting for ICE gathering, like the WHEP player's 3 s. */
  iceGatheringTimeoutMs?: number;
}

export interface WhipPublishOptions {
  whipUrl: string;
  stream: MediaStream;
  iceServers?: RTCIceServer[] | null;
  /** Called on every connectionState change (connected / failed / ...). */
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  signal?: AbortSignal;
}

export interface WhipPublication {
  pc: RTCPeerConnection;
  /** Absolute WHIP resource URL from `Location`, or null if none was sent. */
  resourceUrl: string | null;
}

export type WhipFailureStage = "unsupported" | "offer" | "post" | "answer";

export class WhipError extends Error {
  stage: WhipFailureStage;
  status?: number;
  constructor(stage: WhipFailureStage, message: string, status?: number) {
    super(message);
    this.name = "WhipError";
    this.stage = stage;
    this.status = status;
  }
}

export class WhipAbortedError extends Error {
  constructor() {
    super("WHIP publish aborted");
    this.name = "WhipAbortedError";
  }
}

// Resolved per call, so a test (or a polyfill) can install it late.
const globalFetch: typeof fetch = (input, init) => fetch(input, init);

const defaultCreatePeerConnection = (config?: RTCConfiguration) => {
  const Ctor =
    typeof window !== "undefined"
      ? (window as unknown as { RTCPeerConnection?: typeof RTCPeerConnection })
          .RTCPeerConnection
      : undefined;
  if (!Ctor) throw new WhipError("unsupported", "WebRTC is not supported");
  return new Ctor(config);
};

export const waitForIceGathering = (
  pc: RTCPeerConnection,
  timeoutMs = 3000
): Promise<void> =>
  new Promise<void>((resolve) => {
    if (pc.iceGatheringState === "complete") {
      resolve();
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onState = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    const done = () => {
      if (timer) clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", onState);
      resolve();
    };
    pc.addEventListener("icegatheringstatechange", onState);
    timer = setTimeout(done, timeoutMs);
  });

/** Opus first when the browser lets us order codecs; a no-op otherwise. */
export const preferOpus = (transceiver: RTCRtpTransceiver): void => {
  try {
    const Sender = (
      typeof window !== "undefined"
        ? (window as unknown as { RTCRtpSender?: typeof RTCRtpSender })
            .RTCRtpSender
        : undefined
    ) as typeof RTCRtpSender | undefined;
    const codecs = Sender?.getCapabilities?.("audio")?.codecs;
    if (!codecs?.length || !transceiver.setCodecPreferences) return;
    const isOpus = (c: { mimeType: string }) =>
      c.mimeType.toLowerCase() === "audio/opus";
    const ordered = [
      ...codecs.filter(isOpus),
      ...codecs.filter((c) => !isOpus(c)),
    ];
    transceiver.setCodecPreferences(ordered);
  } catch {
    // Codec ordering is a preference; MediaMTX negotiates Opus anyway.
  }
};

const resolveLocation = (location: string | null, base: string) => {
  if (!location) return null;
  try {
    return new URL(location, base).toString();
  } catch {
    return location;
  }
};

/**
 * Publishes `stream`'s audio track to `whipUrl`. Resolves once the answer
 * is applied (media may still be connecting; watch onConnectionStateChange).
 * On any failure or abort the peer connection is closed before throwing;
 * the caller still owns the stream's tracks.
 */
export const whipPublish = async (
  options: WhipPublishOptions,
  deps: WhipDeps = {}
): Promise<WhipPublication> => {
  const fetchImpl = deps.fetch ?? globalFetch;
  const createPc = deps.createPeerConnection ?? defaultCreatePeerConnection;
  const { whipUrl, stream, iceServers, onConnectionStateChange, signal } =
    options;

  const pc = createPc(iceServers?.length ? { iceServers } : undefined);
  const fail = (error: Error): never => {
    try {
      pc.close();
    } catch {
      // Already closed.
    }
    throw error;
  };
  const checkAborted = () => {
    if (signal?.aborted) fail(new WhipAbortedError());
  };

  pc.onconnectionstatechange = () => {
    onConnectionStateChange?.(pc.connectionState);
  };

  let offerSdp: string;
  try {
    const track = stream.getAudioTracks()[0];
    if (!track) throw new Error("No audio track");
    const transceiver = pc.addTransceiver(track, {
      direction: "sendonly",
      streams: [stream],
    });
    preferOpus(transceiver);
    const offer = await pc.createOffer();
    checkAborted();
    await pc.setLocalDescription(offer);
    await waitForIceGathering(pc, deps.iceGatheringTimeoutMs ?? 3000);
    checkAborted();
    offerSdp = pc.localDescription?.sdp ?? offer.sdp ?? "";
  } catch (reason) {
    if (reason instanceof WhipAbortedError) throw reason;
    return fail(new WhipError("offer", "Couldn't create the audio offer"));
  }

  let response: Response;
  try {
    response = await fetchImpl(whipUrl, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: offerSdp,
      signal,
    });
  } catch {
    checkAborted();
    return fail(new WhipError("post", "Couldn't reach the audio relay"));
  }
  checkAborted();
  if (!response.ok) {
    return fail(
      new WhipError(
        "post",
        `The audio relay refused the stream (${response.status})`,
        response.status
      )
    );
  }

  const resourceUrl = resolveLocation(
    response.headers?.get?.("Location") ?? null,
    whipUrl
  );
  try {
    const answerSdp = await response.text();
    checkAborted();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
  } catch (reason) {
    if (reason instanceof WhipAbortedError) {
      // The relay already holds a resource for this offer: free it.
      if (resourceUrl) void whipDelete(resourceUrl, deps);
      throw reason;
    }
    if (resourceUrl) void whipDelete(resourceUrl, deps);
    return fail(new WhipError("answer", "The audio relay's answer was bad"));
  }
  return { pc, resourceUrl };
};

/** Best-effort DELETE of the WHIP resource; never throws. */
export const whipDelete = async (
  resourceUrl: string,
  deps: Pick<WhipDeps, "fetch"> = {}
): Promise<void> => {
  const fetchImpl = deps.fetch ?? globalFetch;
  try {
    await fetchImpl(resourceUrl, { method: "DELETE" });
  } catch {
    // The relay reaps the session when the peer connection closes anyway.
  }
};
